import '../ServiceableUrls.less';
import { Button, Form, Input, Select } from 'antd';
import React, { useState } from 'react';
import { useAddUrlClusterMutation } from '../../urlClusters/service';
import { PueLevel } from '../../urlClusters/types';
import { PUE_LEVELS } from '../../settings';

const { Option } = Select;

const initialInputState = {
    text: '',
    validationError: '',
};

type Props = {
    websiteGuid: string;
    onFinish: (newClusterGuid: string) => void;
    onCancel: () => void;
};

export default function AddClusterStep({ websiteGuid, onFinish, onCancel }: Props) {
    const [selectedPue, setSelectedPue] = useState<PueLevel>('medium');
    const [titleInputState, setTitleInputState] = useState(initialInputState);
    const [descriptionInputState, setDescriptionInputState] = useState(initialInputState);
    const [pueReasonInput, setPueReasonInput] = useState<string>('');

    const [addUrlCluster, { isLoading: isAddingUrlCluster }] = useAddUrlClusterMutation();

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTitleInputState({ text: event.target.value, validationError: '' });
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDescriptionInputState({ text: event.target.value, validationError: '' });
    };

    const handlePueReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPueReasonInput(event.target.value);
    };

    const handleAddButtonClicked = async () => {
        let hasErrors = false;

        if (!titleInputState.text) {
            setTitleInputState({ ...titleInputState, validationError: 'Please provide a title' });
            hasErrors = true;
        }

        if (!descriptionInputState.text) {
            setDescriptionInputState({ ...descriptionInputState, validationError: 'Please provide a description' });
            hasErrors = true;
        }

        if (hasErrors) {
            return;
        }

        try {
            const addedCluster = await addUrlCluster({
                w_guid: websiteGuid,
                title: titleInputState.text,
                description: descriptionInputState.text,
                pue_level: selectedPue,
                pue_reason: pueReasonInput ? pueReasonInput : undefined,
            }).unwrap();

            onFinish(addedCluster.guid);
        } catch (error) {
            setTitleInputState({ ...titleInputState, validationError: 'Something went wrong! Please try again or contact support.' });
        }
    };

    return (
        <>
            <div>
                <h3>Group Title</h3>
            </div>

            <Form.Item
                validateStatus={titleInputState.validationError ? 'error' : 'validating'}
                help={titleInputState.validationError ? titleInputState.validationError : ''}
            >
                <Input
                    value={titleInputState.text}
                    placeholder="Title"
                    onChange={handleTitleChange}
                ></Input>
            </Form.Item>

            <div>
                <h3>Description</h3>
                <label
                    className="form-label"
                    htmlFor="description-input"
                >
                    A short description of the page group
                </label>
            </div>

            <Form.Item
                validateStatus={descriptionInputState.validationError ? 'error' : 'validating'}
                help={descriptionInputState.validationError ? descriptionInputState.validationError : ''}
            >
                <Input
                    id="description-input"
                    value={descriptionInputState.text}
                    placeholder="Description"
                    onChange={handleDescriptionChange}
                ></Input>
            </Form.Item>

            <div>
                <h3>User Engagement</h3>
                <label
                    className="form-label"
                    htmlFor="pue-select"
                >
                    Select how likely the user is to engage with pages in this group
                </label>
            </div>

            <Form.Item>
                <Select
                    id="pue-select"
                    value={selectedPue}
                    onChange={setSelectedPue}
                >
                    {PUE_LEVELS.map((level) => (
                        <Option
                            key={level.value}
                            value={level.value}
                        >
                            {level.label}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <div>
                <h3>User Engagement Reason</h3>
                <label
                    className="form-label"
                    htmlFor="pue-reason-input"
                >
                    (Optional) A short reason for the engagement level
                </label>
            </div>

            <Form.Item>
                <Input
                    id="pue-reason-input"
                    value={pueReasonInput}
                    placeholder="Reason"
                    onChange={handlePueReasonChange}
                ></Input>
            </Form.Item>

            <div className="form-button-group">
                <Button
                    className="action-cancel-button"
                    type="default"
                    size="small"
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    className="action-add-button"
                    type="primary"
                    size="small"
                    onClick={handleAddButtonClicked}
                    loading={isAddingUrlCluster}
                >
                    Add page group
                </Button>
            </div>
        </>
    );
}
